import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CopyLinkButton from '../../components/common/CopyLinkButton';
import eventsService from '../../api/services/events.service';
import signupsService from '../../api/services/signups.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useSignupsStore, {
  signupLinkUrl,
  slotSubLabel,
  spotsLabel,
  isUnlimited,
  isWindowFull,
  signupFill,
} from '../../store/useSignupsStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate } from '../../utils/formatters';
import { displayPhone } from '../../utils/phone';
import { PATHS } from '../../routes/navConfig';

const ROWS_HEADING = {
  slots: 'Time Windows & Volunteers',
  open: 'Dates & Volunteers',
  items: 'Items & Claims',
};

/* What one row of each kind is called in the totals */
const ROW_UNIT = { slots: 'time window', open: 'date', items: 'item' };

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* Filled / open / full at a glance, above the rows they add up. */
function FillSummary({ signup }) {
  const fill = signupFill(signup);
  if (!fill.rows) return null;
  const items = signup.kind === 'items';
  const unit = ROW_UNIT[signup.kind] || ROW_UNIT.slots;
  return (
    <div className="stat-row" style={{ marginBottom: 14 }}>
      <div className="stat-card">
        <div className="k">{items ? 'Claimed' : 'Filled'}</div>
        <div className="v">{fill.filled}</div>
        <div className="sub">{items ? 'claims so far' : `${fill.filled === 1 ? 'volunteer' : 'volunteers'} signed up`}</div>
      </div>
      <div className="stat-card">
        <div className="k">{items ? 'Still needed' : 'Open'}</div>
        <div className="v">{fill.open}</div>
        <div className="sub">
          {fill.open === 1 ? 'spot left' : 'spots left'}
          {fill.unlimited ? ` · plus ${plural(fill.unlimited, unit)} with no limit` : ''}
        </div>
      </div>
      <div className={`stat-card${fill.full && fill.full === fill.rows ? ' balance' : ''}`}>
        <div className="k">{items ? 'Covered' : 'Full'}</div>
        <div className="v">{fill.full}</div>
        <div className="sub">of {plural(fill.rows, unit)}</div>
      </div>
    </div>
  );
}

/* Who took a row, under it — names and phone for the room parent only; the
   public page shows names alone. */
function People({ list }) {
  if (!list.length) return null;
  return (
    <div style={{ padding: '0 0 10px 4px' }}>
      {list.map((p, i) => (
        <div className="response-row" key={p.id || i}>
          <div className="info">
            <div className="nm">{p.name}</div>
            <div className="ct">
              {displayPhone(p.contact)}
              {p.parentId ? ' · On the app' : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* What's on offer (time windows, dates or items) and send status for one sign-up. */
export default function SignupDetailView({ signup, onEdit, onBack, onDeleted }) {
  const ctx = useContextStore(selectCurrentContext);
  const { patchSignup, removeSignup } = useSignupsStore();
  const navigate = useNavigate();

  const networkId = ctx?.schoolId ? ctx.id : null;
  const url = signupLinkUrl(signup);

  /* The linked event's title is looked up live — the store's list is paginated
     and may not hold the one this sign-up points at. */
  const [linkedEvent, setLinkedEvent] = useState(null);
  useEffect(() => {
    if (!networkId || !signup.eventId) {
      setLinkedEvent(null);
      return undefined;
    }
    let cancelled = false;
    eventsService
      .listEvents(networkId, { page: 1, limit: 100 })
      .then((r) => {
        if (!cancelled) setLinkedEvent(r.events.find((e) => e.id === signup.eventId) || null);
      })
      .catch(() => {
        if (!cancelled) setLinkedEvent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, signup.eventId]);

  /* Send to parents = publish (status true); the same button takes it back to
     draft once it's out. */
  const [statusBusy, setStatusBusy] = useState(false);
  const toggleStatus = async () => {
    setStatusBusy(true);
    try {
      await signupsService.setStatus(signup.id, !signup.status);
      patchSignup(signup.id, { status: !signup.status });
      showToast(signup.status ? 'Sign-up hidden from parents' : 'Sign-up sent to parents ✓');
    } catch (err) {
      showToast(err.message);
    } finally {
      setStatusBusy(false);
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await signupsService.remove(signup.id);
      removeSignup(signup.id);
      setConfirmOpen(false);
      showToast('Sign-up deleted ✓');
      onDeleted ? onDeleted() : onBack();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SectionHead
        title={signup.title}
        sub={
          signup.status
            ? 'Sent to parents — they can open the link and claim a spot.'
            : 'Not sent yet — copy the link, or send it to parents when you\'re ready.'
        }
        actions={
          <>
            <CopyLinkButton url={url} />
            <button className="btn btn-primary btn-sm" disabled={statusBusy} onClick={toggleStatus}>
              {statusBusy ? 'Saving…' : signup.status ? 'Hide from parents' : 'Send to parents'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onEdit}>Edit</button>
            <button className="btn btn-ghost" onClick={onBack}>← Back to Sign-Ups</button>
          </>
        }
      />

      <Card>
        {signup.image && (
          <img
            className="banner"
            src={signup.image}
            alt=""
            style={{ width: '100%', maxWidth: 360, borderRadius: 12, marginBottom: 12 }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <span className={`ev-status ${signup.status ? 'published' : 'draft'}`}>
            {signup.status ? 'Sent to parents' : 'Not sent yet'}
          </span>
          {linkedEvent && (
            <span className="src-chip" style={{ cursor: 'pointer' }} onClick={() => navigate(PATHS.CALENDAR)}>
              Linked to {linkedEvent.title}{linkedEvent.date ? ` · ${fmtDate(linkedEvent.date)}` : ''}
            </span>
          )}
          {signup.acceptsDonations && (
            <span className="src-chip" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>Accepts donations</span>
          )}
        </div>
        {url && (
          <div className="link-copy-row">
            <span className="url">{url}</span>
            <CopyLinkButton url={url} className="btn btn-primary btn-sm" label="Copy Link" />
          </div>
        )}
        <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, marginBottom: 16 }}>
          {signup.desc || 'No description added.'}
        </p>
        <div className="card-head">
          <h3>{ROWS_HEADING[signup.kind] || ROWS_HEADING.slots}</h3>
        </div>
        <FillSummary signup={signup} />
        <div>
          {signup.kind === 'items' ? (
            signup.items.length ? (
              signup.items.map((i) => {
                const left = i.qty - i.claimed.length;
                return (
                  <Fragment key={i.id}>
                    <div className="signup-slot-row">
                      <div className="info">
                        <div className="dt">{i.name}</div>
                      </div>
                      <span className={`cap${left > 0 ? '' : ' full'}`}>
                        {i.claimed.length}/{i.qty} claimed · {left > 0 ? `${left} left` : 'Covered'}
                      </span>
                    </div>
                    <People list={i.claimed} />
                  </Fragment>
                );
              })
            ) : (
              <EmptyState>No items yet.</EmptyState>
            )
          ) : signup.dates.length ? (
            signup.dates.map((d) =>
              d.windows.map((w) => {
                const sub = slotSubLabel(d, w);
                return (
                  <Fragment key={`${d.id}-${w.id}`}>
                    <div className="signup-slot-row">
                      <div className="info">
                        <div className="dt">{fmtDate(d.date)}</div>
                        {sub && <div className="tm">{sub}</div>}
                      </div>
                      <span className={`cap${isWindowFull(w) ? ' full' : ''}`}>
                        {isUnlimited(w)
                          ? `${w.signed.length} signed up · no limit`
                          : `${w.signed.length}/${w.capacity} filled · ${spotsLabel(w)}`}
                      </span>
                    </div>
                    <People list={w.signed} />
                  </Fragment>
                );
              })
            )
          ) : (
            <EmptyState>{signup.kind === 'open' ? 'No dates yet.' : 'No time windows yet.'}</EmptyState>
          )}
        </div>
        <div className="inline-form-actions" style={{ borderTop: 'none', justifyContent: 'flex-start', paddingTop: 14 }}>
          <button className="btn btn-ghost" style={{ color: 'var(--red)' }} onClick={() => setConfirmOpen(true)}>
            Delete Sign-Up
          </button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Sign-Up"
        confirmLabel="Delete Sign-Up"
        busy={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      >
        <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>
          {signup.title}
        </div>
        <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
          This removes the sign-up and stops its link from working — anyone who already has it will see nothing.
          This can't be undone.
        </p>
      </ConfirmDialog>
    </>
  );
}
